import { useMemo, useState } from "react";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { Users01 } from "@untitledui/icons";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge } from "@/components/base/badges/badges";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import { Button } from "@/components/base/buttons/button";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { StatCard } from "@/components/application/metrics/stat-card";
// useToast zatím bez nativního ekvivalentu v scope guidu → zůstává legacy barrel.
import { useToast } from "@/components/application/notifications/toast-provider";
import { useAuthStore } from "../../lib/authStore";
import {
  useMicrogroupDissolve,
  useMicrogroupOverview,
} from "../../hooks/useMicrogroupQueries";
import {
  MicrogroupDissolveConfirmDialog,
} from "../../components/tenant/MicrogroupDissolveConfirmDialog";
import type {
  MicrogroupMemberRow,
  MicrogroupMemberStatus,
} from "../../schemas/microgroup";
import { type MicrogroupDissolveInput } from "../../schemas/microgroupDissolve";
import { formatKc } from "../../schemas/budget";

/**
 * UC-006 Mikroskupina Settings page — sub-admin (Milan) Settings + dissolve.
 *
 * Sub-admin Milan vidí full drill-down (license_plate + email + status +
 * joined_at + consumption_mtd) per princip #3 fair-split transparency.
 * Běžný member (endUser Zdeněk) by viděl pouze vlastní řádek + agregát —
 * data segregation per princip #1 + #6, backend dělá filtering, frontend
 * jen renderuje, co dostal.
 *
 * Akce:
 *   - "Pozvat nového člena" CTA → `/tenant/microgroup/invite` (subAdmin only)
 *   - "Zrušit mikroskupinu" destructive button (subAdmin only) → 3-stupňový
 *     Confirm Dialog s "ZRUŠIT" exact-match per princip #5.
 */

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/microgroup",
  component: MicrogroupIndexComponent,
});

const STATUS_COLOR: Record<MicrogroupMemberStatus, BadgeColors> = {
  active: "success",
  removed: "gray",
  withdrawn: "error",
};

const STATUS_LABEL: Record<MicrogroupMemberStatus, string> = {
  active: "Aktivní",
  removed: "Odstraněn",
  withdrawn: "Odvolal souhlas",
};

function MicrogroupIndexComponent() {
  const auth = useAuthStore((s) => s.auth);
  const navigate = useNavigate();
  const toast = useToast();
  const overview = useMicrogroupOverview();

  const role = auth.state === "authenticated" ? auth.user.role : "endUser";
  const isSubAdmin = role === "subAdmin" || role === "tenantAdmin";

  const dissolveMutation = useMicrogroupDissolve(overview.data?.microgroup_id);
  const [dissolveOpen, setDissolveOpen] = useState(false);

  const columns: Column<MicrogroupMemberRow>[] = useMemo(() => {
    const cols: Column<MicrogroupMemberRow>[] = [
      {
        key: "name",
        header: "Člen",
        cell: (row) => (
          <div>
            <strong>{row.invited_name}</strong>
            {isSubAdmin && row.invited_email && (
              <div className="text-sm text-tertiary">{row.invited_email}</div>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Stav",
        cell: (row) => (
          <Badge color={STATUS_COLOR[row.status]}>
            {STATUS_LABEL[row.status]}
          </Badge>
        ),
      },
    ];
    if (isSubAdmin) {
      cols.push({
        key: "joined_at",
        header: "Aktivní od",
        cell: (row) =>
          row.joined_at
            ? new Date(row.joined_at).toLocaleDateString("cs-CZ", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            : "—",
      });
      cols.push({
        key: "consumption",
        header: "Spotřeba MTD",
        cell: (row) => formatKc(row.consumption_mtd_kc),
        className: "text-right tabular-nums",
      });
      cols.push({
        key: "drilldown",
        header: "Transakce",
        cell: (row) =>
          row.drilldown_transactions_count != null
            ? row.drilldown_transactions_count
            : "—",
        className: "text-right tabular-nums",
      });
    }
    return cols;
  }, [isSubAdmin]);

  function handleDissolveSubmit(input: MicrogroupDissolveInput) {
    dissolveMutation.mutate(input, {
      onSuccess: () => {
        setDissolveOpen(false);
        toast.push({
          variant: "success",
          message: "Mikroskupina zrušena, karty v Axigonu zneplatní Petr do 48 h.",
        });
        navigate({ to: "/tenant/dashboard" });
      },
    });
  }

  if (overview.isLoading) {
    return (
      <main className="ui-route-main" aria-labelledby="microgroup-heading">
        <h1 id="microgroup-heading">Mikroskupina</h1>
        <LoadingIndicator label="Načítám mikroskupinu…" />
      </main>
    );
  }
  if (overview.isError) {
    return (
      <main className="ui-route-main" aria-labelledby="microgroup-heading">
        <h1 id="microgroup-heading">Mikroskupina</h1>
        <InlineAlert variant="error" title="Načítání selhalo">
          Mikroskupinu se nepodařilo načíst. Obnovte stránku, prosím.
        </InlineAlert>
      </main>
    );
  }
  // data === null → uživatel není sub-adminem žádné mikroskupiny (backend 404
  // namapovaný na null v useMicrogroupOverview). Normální stav, ne chyba.
  if (!overview.data) {
    return (
      <main className="ui-route-main" aria-labelledby="microgroup-heading">
        <h1 id="microgroup-heading">Mikroskupina</h1>
        <EmptyState size="sm">
          <EmptyState.Header>
            <EmptyState.FeaturedIcon color="gray" icon={Users01} />
          </EmptyState.Header>
          <EmptyState.Content>
            <EmptyState.Title>Žádná mikroskupina k zobrazení</EmptyState.Title>
            <EmptyState.Description>
              Nejste sub-adminem žádné mikroskupiny.
            </EmptyState.Description>
          </EmptyState.Content>
        </EmptyState>
      </main>
    );
  }

  const data = overview.data;

  return (
    <main className="ui-route-main" aria-labelledby="microgroup-heading">
      <header className="ui-route-header">
        <h1 id="microgroup-heading">Mikroskupina — {data.microgroup_name}</h1>
        {isSubAdmin && (
          <div className="ui-route-actions">
            <Button
              color="primary"
              href="/tenant/microgroup/invite"
              data-testid="microgroup-invite-cta"
            >
              Pozvat nového člena
            </Button>
            <Button
              color="primary-destructive"
              onPress={() => setDissolveOpen(true)}
              data-testid="microgroup-dissolve-trigger"
            >
              Zrušit mikroskupinu
            </Button>
          </div>
        )}
      </header>

      <div className="ui-stat-grid">
        <StatCard
          label="Aktivní členové"
          value={`${data.active_member_count} / ${data.member_limit}`}
        />
        <StatCard
          label="Otevřené pozvánky"
          value={String(data.pending_invitation_count)}
        />
      </div>

      <Card>
        <CardHeader title="Členové" />
        <CardBody>
          {data.members.length === 0 ? (
            <EmptyState size="sm">
              <EmptyState.Header>
                <EmptyState.FeaturedIcon color="gray" icon={Users01} />
              </EmptyState.Header>
              <EmptyState.Content>
                <EmptyState.Title>Žádní členové</EmptyState.Title>
                <EmptyState.Description>
                  {isSubAdmin
                    ? "Začněte pozváním prvního člena."
                    : "Vaše mikroskupina zatím nemá žádné členy."}
                </EmptyState.Description>
              </EmptyState.Content>
            </EmptyState>
          ) : (
            <DataTable
              caption="Členové mikroskupiny"
              columns={columns}
              rows={data.members}
              rowKey={(r) => r.member_id}
              emptyMessage="Žádní členové."
            />
          )}
        </CardBody>
      </Card>

      <MicrogroupDissolveConfirmDialog
        open={dissolveOpen}
        microgroupName={data.microgroup_name}
        activeMemberCount={data.active_member_count}
        submitting={dissolveMutation.isPending}
        submitError={
          dissolveMutation.isError && dissolveMutation.error instanceof Error
            ? dissolveMutation.error.message
            : null
        }
        onCancel={() => setDissolveOpen(false)}
        onConfirm={handleDissolveSubmit}
      />
    </main>
  );
}
